import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'

import { StatusTag } from '../../../../../components/tableTemplates'
import { calculateBreakpointWidth } from '../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../utils/wrapper'
import { useConfigContext } from '../../../../ui-config/context/useConfigContext'
import {
  useInvoiceAllowanceChargeById,
  useInvoiceById,
} from '../../../data/hooks'

type Props = {
  invoiceId: string
  id: string
}

export const InfoAllowanceCharge: React.FC<Props> = ({ invoiceId, id }) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()

  const { data: invoice } = useInvoiceById(invoiceId)
  const { data: invoiceAllowanceCharge, isLoading } =
    useInvoiceAllowanceChargeById(invoiceId, id)

  const [visible, setVisible] = useState(false)

  const currency = invoice?.Payload.OrderCurrency

  // ************************************************************
  // components
  // ************************************************************
  // alcType
  const alcTypeLabel = <label htmlFor="alcType" className="text-sm"></label>

  const alcTypeComponent = (
    <InputText
      id="alcType"
      className="w-full"
      value={t(
        invoiceAllowanceCharge?.AlcType === 'Surcharge'
          ? t('common.codes.alcType.surcharge')
          : t('common.codes.alcType.discount'),
      )}
      disabled
    />
  )

  const alcTypeWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoAllowanceCharge}
      component={alcTypeComponent}
      label={alcTypeLabel}
      isLoading={isLoading}
    />
  )

  // descriptionText
  const descriptionTextLabel = (
    <label htmlFor="descriptionText" className="text-sm"></label>
  )

  const descriptionTextComponent = (
    <InputText
      id="descriptionText"
      className="w-full"
      value={invoiceAllowanceCharge?.Description?.Text}
      disabled
    />
  )

  const descriptionTextWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoAllowanceCharge}
      component={descriptionTextComponent}
      label={descriptionTextLabel}
      isLoading={isLoading}
    />
  )

  // percentage
  const percentageLabel = (
    <label htmlFor="percentage" className="text-sm"></label>
  )

  const percentageComponent = (
    <InputNumber
      id="percentage"
      value={invoiceAllowanceCharge?.Percentage}
      className="w-full"
      suffix=" %"
      minFractionDigits={2}
      locale={language}
      disabled
    />
  )

  const percentageWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoAllowanceCharge}
      component={percentageComponent}
      label={percentageLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPercentage
  const confirmationPercentageLabel = (
    <label htmlFor="confirmationPercentage" className="text-sm"></label>
  )

  const confirmationPercentageComponent = (
    <InputNumber
      id="confirmationPercentage"
      value={invoiceAllowanceCharge?.ConfirmationPercentage}
      className="w-full"
      min={0}
      suffix=" %"
      minFractionDigits={2}
      showButtons
      locale={language}
      disabled
    />
  )

  const confirmationPercentageWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoAllowanceCharge}
      component={confirmationPercentageComponent}
      label={confirmationPercentageLabel}
      isLoading={isLoading}
    />
  )

  // amount
  const amountLabel = <label htmlFor="amount" className="text-sm"></label>

  const amountComponent = (
    <InputNumber
      id="amount"
      mode="currency"
      currency={currency}
      value={invoiceAllowanceCharge?.Amount}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const amountWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoAllowanceCharge}
      component={amountComponent}
      label={amountLabel}
      isLoading={isLoading}
    />
  )

  // confirmationAmount
  const confirmationAmountLabel = (
    <label htmlFor="confirmationAmount" className="text-sm"></label>
  )

  const confirmationAmountComponent = (
    <InputNumber
      id="confirmationAmount"
      mode="currency"
      showButtons
      currency={currency}
      value={invoiceAllowanceCharge?.ConfirmationAmount}
      className="w-full"
      min={0}
      locale={language}
      disabled
    />
  )

  const confirmationAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoAllowanceCharge}
      component={confirmationAmountComponent}
      label={confirmationAmountLabel}
      isLoading={isLoading}
    />
  )

  // ************************************************************
  // dialog components
  // ************************************************************
  // header
  const header = (
    <div className="flex align-content-center flex-wrap">
      <span className="mr-3">
        {t('invoice.allowanceCharges.table.info.dialog.title')}
      </span>

      <StatusTag status={invoiceAllowanceCharge?.Status} />
    </div>
  )

  // footer
  const footer = (
    <Button
      label={t('common.buttons.back')}
      icon="pi pi-check"
      onClick={() => setVisible(false)}
      autoFocus
      className="mt-3 w-full md:w-auto"
    />
  )

  return (
    <>
      <Button
        icon="pi pi-info-circle"
        className="p-button-rounded p-button-text mr-2"
        onClick={() => {
          setVisible(true)
        }}
        visible={config.invoice.infoAllowanceCharge.enabled !== false}
      />

      <Dialog
        header={header}
        visible={visible}
        style={{ width: '40rem' }}
        breakpoints={{ [calculateBreakpointWidth(40)]: '90vw' }}
        footer={footer}
        onHide={() => setVisible(false)}
        blockScroll
      >
        {/* alcType */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{alcTypeWrapper}</div>
        </div>

        <Divider className="md:hidden" />

        {/* descriptionText */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{descriptionTextWrapper}</div>
        </div>

        <Divider className="md:hidden" />

        {/* percentage */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{percentageWrapper}</div>
          <div className="col-12 md:col-6">{confirmationPercentageWrapper}</div>
        </div>

        <Divider className="md:hidden" />

        {/* amount */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{amountWrapper}</div>
          <div className="col-12 md:col-6">{confirmationAmountWrapper}</div>
        </div>
      </Dialog>
    </>
  )
}
